import type * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Grilles de page (console.css). Écarts : 16px entre indicateurs, 24px entre
 * cartes et sections. Les grilles à deux colonnes passent sur une colonne
 * sous 1100px.
 */

type DivProps = React.HTMLAttributes<HTMLElement> & {
  /** Élément rendu : `section` (défaut, avec `aria-label`) ou `div`. */
  as?: 'section' | 'div';
};

/** `row-4` : rangée d'indicateurs (StatCard), colonnes de 220px minimum. */
function Row4({ as = 'section', className, ...props }: DivProps): React.JSX.Element {
  const Comp = as;
  return (
    <Comp
      className={cn(
        'grid grid-cols-[repeat(auto-fit,minmax(min(220px,100%),1fr))] gap-4',
        className,
      )}
      {...props}
    />
  );
}

/** `split-21` : deux tiers / un tiers. */
function Split21({ as = 'section', className, ...props }: DivProps): React.JSX.Element {
  const Comp = as;
  return (
    <Comp
      className={cn(
        'grid grid-cols-1 gap-6 min-[1100px]:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]',
        className,
      )}
      {...props}
    />
  );
}

/** `split-12` : un tiers / deux tiers. */
function Split12({ as = 'section', className, ...props }: DivProps): React.JSX.Element {
  const Comp = as;
  return (
    <Comp
      className={cn(
        'grid grid-cols-1 gap-6 min-[1100px]:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]',
        className,
      )}
      {...props}
    />
  );
}

/** `split-eq` : deux colonnes égales. */
function SplitEq({ as = 'section', className, ...props }: DivProps): React.JSX.Element {
  const Comp = as;
  return (
    <Comp className={cn('grid grid-cols-1 gap-6 min-[1100px]:grid-cols-2', className)} {...props} />
  );
}

/** `stack` : pile verticale, écart 24px (ou `gap-4` via className). */
function Stack({ as = 'div', className, ...props }: DivProps): React.JSX.Element {
  const Comp = as;
  return <Comp className={cn('grid content-start gap-6', className)} {...props} />;
}

export { Row4, Split21, Split12, SplitEq, Stack };
